'use client';

import { useState } from 'react';

import Icon from './Icon';

export type FlashMarqueeProps = {
  flash: {
    enabled: boolean;
    type: 'info' | 'warning' | 'error' | 'success';
    message: string;
    dismissible: boolean;
  };
  speed?: number; // in seconds (default: 20)
  pauseOnHover?: boolean;
  repeat?: number; // how many times to repeat the message (default: 2)
};

const FlashMarquee = ({ flash, speed = 30, pauseOnHover = true, repeat = 1 }: FlashMarqueeProps) => {
  const [isDismissed, setIsDismissed] = useState(false);

  if (!flash?.enabled || !flash?.message || isDismissed) return null;

  const typeStyles = {
    info: 'bg-primary-600/40 text-white',
    warning: 'bg-warning-500 text-black',
    error: 'bg-error-600 text-white',
    success: 'bg-success-600 text-white',
  };

  const typeIcons = {
    info: 'info',
    warning: 'warning',
    error: 'error',
    success: 'success',
  };

  // Create repeated message array
  const messages = Array(repeat).fill(flash.message);

  return (
    <div className={`relative w-full overflow-hidden ${typeStyles[flash.type]} py-1`} role="alert" aria-live="polite">
      <div className="relative flex items-center">
        {/* Marquee Container */}
        <div
          className={`flex whitespace-nowrap ${pauseOnHover ? 'hover:pause-animation' : ''}`}
          style={{
            animation: `marquee ${speed}s linear infinite`,
          }}
        >
          {messages.map((msg, index) => (
            <div key={index} className="flex items-center mx-8">
              <span className="text-md mr-2" aria-hidden="true">
                <Icon name="card" className="w-5 h-5 md:w-6 md:h-6" />
              </span>
              <span className="text-xs md:text-sm font-medium">{msg}</span>
            </div>
          ))}
          {/* Duplicate for seamless loop */}
          {messages.map((msg, index) => (
            <div key={`duplicate-${index}`} className="flex items-center mx-8" aria-hidden="true">
              <span className="text-md mr-2">
                <Icon name="card" className="w-5 h-5 md:w-6 md:h-6" />
              </span>
              <span className="text-xs md:text-sm font-medium">{msg}</span>
            </div>
          ))}
        </div>

        {/* Dismiss Button */}
        {flash.dismissible && (
          <button
            onClick={() => setIsDismissed(true)}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 flex flex-row items-center justify-center rounded-full hover:bg-black/10 transition-colors"
            aria-label="Dismiss notification"
          >
            <Icon name="close" className="w-4 h-4 md:w-5 md:h-5" />
          </button>
        )}
      </div>

      {/* CSS Animation Styles */}
      <style>{`
        @keyframes marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }

        .hover\\:pause-animation:hover {
          animation-play-state: paused !important;
        }
      `}</style>
    </div>
  );
};

export default FlashMarquee;
